/**
 * Address Value Object
 * 
 * Domain concept: "Where is this property located?"
 * 
 * Business rules:
 * - All address components are required (street, city, province)
 * - Unit number is optional
 * 
 * Computed facts:
 * - hasValue: Whether the address has required components
 * - hasUnit: Whether a unit number exists
 * 
 * Usage:
 * - Format: src/ui/address/format-address.ts
 * - UI: src/ui/address/hero-address.tsx
 */
export class Address {
  readonly #street: string;
  readonly #city: string;
  readonly #province: string;
  readonly #unitNumber?: string;

  private constructor(
    street: string,
    city: string,
    province: string,
    unitNumber?: string
  ) {
    this.#street = street;
    this.#city = city;
    this.#province = province;
    this.#unitNumber = unitNumber;
  }

  /**
   * Creates an Address from raw data.
   */
  static from(data: {
    street: string;
    city: string;
    province: string;
    unitNumber?: string;
  }): Address {
    return new Address(
      data.street,
      data.city,
      data.province,
      data.unitNumber
    );
  }

  /**
   * Gets the street address.
   */
  get street(): string {
    return this.#street;
  }

  /**
   * Gets the city.
   */
  get city(): string {
    return this.#city;
  }

  /**
   * Gets the province/state.
   */
  get province(): string {
    return this.#province;
  }

  /**
   * Gets the unit number if present.
   */
  get unitNumber(): string | undefined {
    return this.#unitNumber;
  }

  /**
   * Whether the address has all required components.
   * Always true for Address since constructor requires them.
   */
  get hasValue(): boolean {
    return !!this.#street && !!this.#city && !!this.#province;
  }

  /**
   * Checks if this address has a unit number.
   * Example: Condos, apartments show "Unit 505 - 123 Main St"
   */
  get hasUnit(): boolean {
    return !!this.#unitNumber;
  }
}
